import { useEffect, useMemo, useRef, useState } from "react";

export default function MorseBeacon({
  active,
  unit = 300,

  // local reset (J1)
  resetSignal = 0,

  // sync (spectateurs)
  syncStartedAt,       // ISO string | Date | null
  syncResetCounter = 0,
  serverNow,           // ISO string (optionnel)
  hideResetLabel = false,
  className = "",
}) {
  const phrase = "Miragio";
  const LETTER_GAP_UNITS = 8;
  const SYMBOL_GAP_UNITS = 1;
  const WORD_GAP_UNITS = 10;

  const pattern = useMemo(() => {
    if (!phrase) return [];

    const alphabet = {
      a: ".-", b: "-...", c: "-.-.", d: "-..",
      e: ".", f: "..-.", g: "--.", h: "....",
      i: "..", j: ".---", k: "-.-", l: ".-..",
      m: "--", n: "-.", o: "---", p: ".--.",
      q: "--.-", r: ".-.", s: "...", t: "-",
      u: "..-", v: "...-", w: ".--", x: "-..-",
      y: "-.--", z: "--..",
      "1": ".----", "2": "..---", "3": "...--", "4": "....-",
      "5": ".....", "6": "-....", "7": "--...", "8": "---..",
      "9": "----.", "0": "-----",
      " ": "/",
    };

    const morseLetters = phrase
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .split("")
      .map((ch) => alphabet[ch])
      .filter(Boolean);

    const steps = [];

    for (const letter of morseLetters) {
      if (letter === "/") {
        steps.push({ on: false, duration: WORD_GAP_UNITS * unit });
        continue;
      }

      for (const symbol of letter) {
        if (symbol === ".") {
          steps.push({ on: true, color: "#44fbf6", duration: unit });
          steps.push({ on: false, duration: SYMBOL_GAP_UNITS * unit });
        } else if (symbol === "-") {
          steps.push({ on: true, color: "#44fbf6", duration: 3 * unit });
          steps.push({ on: false, duration: SYMBOL_GAP_UNITS * unit });
        }
      }

      steps.push({ on: false, duration: LETTER_GAP_UNITS * unit });
    }

    // flash reset visuel
    steps.push({ on: true, color: "blue", duration: 4 * unit });
    steps.push({ on: false, duration: 2 * unit });

    return steps;
  }, [phrase, unit]);

  const resetLatchUntilRef = useRef(0);

  // index de la frame RESET (= avant-dernière: blue)
  const resetIndex = useMemo(() => Math.max(0, pattern.length - 2), [pattern.length]);

  // durée cumulée avant la frame RESET (pour démarrer directement dessus en sync)
  const offsetToResetMs = useMemo(() => {
    if (!pattern.length) return 0;
    let acc = 0;
    for (let i = 0; i < resetIndex; i++) acc += pattern[i].duration || 0;
    return acc;
  }, [pattern, resetIndex]);

  // ---------- UI state ----------
  const [stepIndex, setStepIndex] = useState(0);
  const [isOn, setIsOn] = useState(false);
  const [color, setColor] = useState("#44fbf6");

  // ---------- server clock offset ----------
  const offsetRef = useRef(0);
  useEffect(() => {
    if (!serverNow) return;
    const serverMs = new Date(serverNow).getTime();
    if (Number.isNaN(serverMs)) return;
    offsetRef.current = serverMs - Date.now();
  }, [serverNow]);

  // ---------- helpers for sync mode ----------
  const totalDuration = useMemo(() => {
    return pattern.reduce((sum, s) => sum + (s.duration || 0), 0);
  }, [pattern]);

  function getStepIndexAt(elapsedMs) {
    if (!pattern.length || !totalDuration) return 0;
    const t = ((elapsedMs % totalDuration) + totalDuration) % totalDuration;

    let acc = 0;
    for (let i = 0; i < pattern.length; i++) {
      acc += pattern[i].duration;
      if (t < acc) return i;
    }
    return 0;
  }

  useEffect(() => {
    if (!syncStartedAt) return; // latch utile en mode sync
    // on force un RESET visible pendant 600ms
    resetLatchUntilRef.current = Date.now() + 600;
  }, [syncResetCounter, syncStartedAt]);


  // =========================================================
  // MODE 1 : SYNC (si syncStartedAt est fourni)
  // =========================================================
  useEffect(() => {
    if (!active || !pattern.length) {
      setIsOn(false);
      return;
    }

    if (!syncStartedAt) return; // on laisse le mode local gérer

    const startMs = new Date(syncStartedAt).getTime();
    if (Number.isNaN(startMs)) return;

    //  on décale le start pour que t=0 => frame RESET (bleue)
    const effectiveStartMs = startMs - offsetToResetMs;

    let rafId = null;
    const tick = () => {
      const clientNow = Date.now();
      const now = clientNow + (offsetRef.current || 0);

      // latch visuel RESET (prioritaire)
      if (clientNow < (resetLatchUntilRef.current || 0)) {
        setIsOn(true);
        setColor("blue");
        rafId = requestAnimationFrame(tick);
        return;
      }

      const elapsed = now - effectiveStartMs;

      const idx = getStepIndexAt(elapsed);
      const step = pattern[idx];

      setIsOn(!!step.on);
      setColor(step.color || "#44fbf6");

      rafId = requestAnimationFrame(tick);
    };


    tick();
    return () => rafId && cancelAnimationFrame(rafId);
  }, [active, pattern, totalDuration, syncStartedAt, syncResetCounter, offsetToResetMs]);

  // =========================================================
  // MODE 2 : LOCAL (si PAS de syncStartedAt)
  // =========================================================
  useEffect(() => {
    if (syncStartedAt) return; // sync prend la main

    if (!active || pattern.length === 0) {
      setIsOn(false);
      return;
    }

    const step = pattern[stepIndex];
    setIsOn(step.on);
    setColor(step.color || "#44fbf6");

    const timer = setTimeout(() => {
      setStepIndex((i) => (i + 1) % pattern.length);
    }, step.duration);

    return () => clearTimeout(timer);
  }, [active, pattern, stepIndex, syncStartedAt]);

  // reset local (bouton)
  useEffect(() => {
    if (syncStartedAt) return; // en sync, le reset vient du serveur
    if (!pattern.length) return;

    setStepIndex(resetIndex); // on saute sur RESET
  }, [resetSignal, syncStartedAt, pattern.length, resetIndex]);


  return (
    <div
      className={[
        "pointer-events-none absolute flex items-center justify-center lightCode",
        className,
      ].join(" ")}
    >
      <div
        className={`
          w-full h-full transition-all duration-150 rounded
          ${isOn
            ? color === "blue"
              ? "bg-transparent"
              : "bg-[#44fbf6] shadow-[0_0_20px_rgba(100,170,255,1)]"
            : "bg-transparent shadow-none"
          }
        `}
      >
        {!hideResetLabel && isOn && color === "blue" && (
          <span className="absolute inset-0 flex items-center justify-center text-[15px] tracking-wide text-[#e370e9] font-semibold">
            ↻ RESET
          </span>
        )}
      </div>
    </div>
  );
}
